/**
 * FHIR write payloads for the supervision Document Review page.
 */
import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import type { SignatureInfo } from '../../appointments/fhirNoteMapping';
import {
  applySupervisorCosignature,
  getAppointmentIdFromQR,
  removeRequestedEditsFromQR,
  removeSignatureFromQR,
  setDetailedStatus,
  setRequestedEditsOnQR,
  setSignatureOnQR,
  PROVIDER_SIGNATURE_EXT_URL,
} from '../../appointments/fhirNoteMapping';
import { commitSignedNoteAndSealAppointment } from '../../appointments/utils/attendanceFulfillment';
import { fhirUpdateQR } from '../../appointments/utils/fhirWrite';
import { getParticipantAttendanceFromResponseItems } from '../../appointments/utils/participantAttendance';
import { SUPERVISION_MEETING_TAG_SYSTEM } from '../supervisionFhirConstants';
import {
  getChartDocumentStatus,
  isChartDocumentQuestionnaireResponse,
  setChartDocumentStatus,
} from '@/lib/chart/chartDocumentWorkflow';

import type { SupervisionQueueItemWithDocumentId } from '../types';

export function getQueueDocumentReviewId(item: SupervisionQueueItemWithDocumentId): string {
  return item.documentIdForReview ?? item.appointmentId ?? item.id;
}

/** Provider sign from document review (appointment notes, chart documents, supervision meeting notes). */
export function applyProviderSignatureForDocumentReview(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  let updated = setSignatureOnQR(qr, PROVIDER_SIGNATURE_EXT_URL, signature);
  updated = removeRequestedEditsFromQR(updated);

  if (isChartDocumentQuestionnaireResponse(updated)) {
    return setChartDocumentStatus(updated, 'completed');
  }

  return {
    ...updated,
    status: 'completed',
    extension: setDetailedStatus(updated.extension ?? [], 'signed'),
  };
}

/**
 * Supervisor Request Edits. Besides removing the signature and flagging the note, the
 * detailed status moves to `needs-edits`: a `signed` status would keep the appointment's
 * attendance lock in place, so the provider's corrected attendance could never apply.
 */
export function applyRequestedEditsForDocumentReview(qr: QuestionnaireResponse): QuestionnaireResponse {
  let updated = removeSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
  updated = setRequestedEditsOnQR(updated);
  if (isChartDocumentQuestionnaireResponse(updated)) {
    return setChartDocumentStatus(updated, 'draft');
  }
  return { ...updated, extension: setDetailedStatus(updated.extension ?? [], 'needs-edits') };
}

function isSupervisionMeetingNote(qr: QuestionnaireResponse): boolean {
  return qr.meta?.tag?.some(
    (tag) => tag.system === SUPERVISION_MEETING_TAG_SYSTEM && tag.code === 'true',
  ) ?? false;
}

/**
 * Appointment notes are signed and sealed through `commitSignedNoteAndSealAppointment` (the admin
 * `sign-and-seal-appointment` action writes the signature, then the seal); chart documents and supervision
 * meeting notes stay on the raw QR write.
 */
export async function saveProviderSignedDocument(
  medplum: MedplumClient,
  signed: QuestionnaireResponse & { id: string },
): Promise<QuestionnaireResponse> {
  const appointmentId = getAppointmentIdFromQR(signed);
  if (appointmentId && !isSupervisionMeetingNote(signed) && !isChartDocumentQuestionnaireResponse(signed)) {
    return commitSignedNoteAndSealAppointment(
      medplum,
      signed,
      appointmentId,
      getParticipantAttendanceFromResponseItems(signed.item),
    );
  }
  return fhirUpdateQR(medplum, signed);
}

/**
 * Supervisor co-sign from document review.
 * Ensures chart documents regain `chart-document-status: completed` when re-signed after Request Edits.
 */
export function applySupervisorCosignatureForDocumentReview(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  let updated = qr;
  if (
    isChartDocumentQuestionnaireResponse(updated)
    && getChartDocumentStatus(updated) !== 'completed'
  ) {
    updated = setChartDocumentStatus(updated, 'completed');
  }
  return applySupervisorCosignature(updated, signature);
}
